import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, X, ChevronLeft, ChevronRight, ZoomIn } from 'lucide-react';
import { Button } from '../components/ui/button';
import { portfolioItems } from '../mock';

export const PortfolioPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('Tous');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const categories = ['Tous', ...new Set(portfolioItems.map(item => item.category))];

  const filteredItems = selectedCategory === 'Tous'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === selectedCategory);

  const closeLightbox = useCallback(() => setLightboxIndex(null), []);
  const showPrev = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i - 1 + filteredItems.length) % filteredItems.length));
  }, [filteredItems.length]);
  const showNext = useCallback(() => {
    setLightboxIndex((i) => (i === null ? null : (i + 1) % filteredItems.length));
  }, [filteredItems.length]);

  useEffect(() => {
    if (lightboxIndex === null) return;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [lightboxIndex, closeLightbox, showPrev, showNext]);

  const currentItem = lightboxIndex !== null ? filteredItems[lightboxIndex] : null;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-gray-50 to-white py-20 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Nos Réalisations
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            Découvrez quelques-uns de nos projets récemment réalisés avec passion et professionnalisme
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-4 bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3 justify-center">
            {categories.map((category) => (
              <button
                key={category}
                data-testid={`filter-${category}`}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                  selectedCategory === category
                    ? 'bg-gray-800 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Portfolio Grid */}
      <section className="py-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredItems.map((item, index) => (
              <button
                key={item.id}
                type="button"
                data-testid={`portfolio-item-${item.id}`}
                onClick={() => setLightboxIndex(index)}
                className="group text-left cursor-pointer focus:outline-none focus:ring-2 focus:ring-gray-800 focus:ring-offset-2 rounded-lg"
              >
                <div className="relative overflow-hidden rounded-lg mb-4 bg-gray-100 aspect-[4/3]">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect width="400" height="300" fill="%23e5e7eb"/%3E%3Ctext x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle" font-family="sans-serif" font-size="18" fill="%239ca3af"%3EImage de démonstration%3C/text%3E%3C/svg%3E';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="bg-white/90 rounded-full p-3 transform scale-90 group-hover:scale-100 transition-transform">
                      <ZoomIn size={22} className="text-gray-900" />
                    </div>
                  </div>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">{item.category}</p>
                  <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-gray-700 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
                </div>
              </button>
            ))}
          </div>

          {filteredItems.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-gray-500">Aucune réalisation dans cette catégorie pour le moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Votre projet sera notre prochaine réalisation
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Contactez-nous pour transformer vos idées en réalité
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-gray-800 hover:bg-gray-700 text-white" data-testid="cta-devis-btn">
              Demander un devis
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {currentItem && (
        <div
          data-testid="lightbox-overlay"
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 animate-in fade-in duration-200"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={currentItem.title}
        >
          <button
            type="button"
            data-testid="lightbox-close"
            onClick={(e) => { e.stopPropagation(); closeLightbox(); }}
            className="absolute top-4 right-4 md:top-6 md:right-6 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 transition-colors"
            aria-label="Fermer"
          >
            <X size={28} />
          </button>

          {filteredItems.length > 1 && (
            <>
              <button
                type="button"
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); showPrev(); }}
                className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 md:p-3 transition-colors"
                aria-label="Précédent"
              >
                <ChevronLeft size={32} />
              </button>
              <button
                type="button"
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); showNext(); }}
                className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2 md:p-3 transition-colors"
                aria-label="Suivant"
              >
                <ChevronRight size={32} />
              </button>
            </>
          )}

          <div
            className="relative max-w-6xl w-full flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={currentItem.image}
              alt={currentItem.title}
              className="max-h-[80vh] w-auto max-w-full object-contain rounded-lg shadow-2xl"
            />
            <div className="mt-4 text-center text-white">
              <p className="text-sm text-gray-300 mb-1">{currentItem.category}</p>
              <h3 className="text-xl md:text-2xl font-bold">{currentItem.title}</h3>
              <p className="text-sm text-gray-300 mt-2 max-w-2xl mx-auto">{currentItem.description}</p>
              {filteredItems.length > 1 && (
                <p className="text-xs text-gray-400 mt-3">
                  {lightboxIndex + 1} / {filteredItems.length}
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
